/**
 * `/v1/platform/messaging/sms` og `/v1/platform/organizations/:id/sms-usage` — SMS-bryteren og
 * telleren i Control Room (jobb 3, 0495). Plattformens, utenfor hvert hus: ingen organisasjonsheader.
 * En snudd bryter friskes opp i loggen og i det dialogene spør om (`messagingChannels`).
 */
import {
  platformSmsSettingResponseSchema,
  smsUsageResponseSchema,
  type PlatformSmsSettingResponse,
  type SmsUsageResponse,
  type UpdatePlatformSmsSettingRequest,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

const SETTING_PATH = '/platform/messaging/sms';

export function controlRoomSmsQueryOptions() {
  return queryOptions({
    queryKey: qk.controlRoomSms(),
    queryFn: ({ signal }): Promise<PlatformSmsSettingResponse> =>
      apiRequest({
        path: SETTING_PATH,
        schema: platformSmsSettingResponseSchema,
        sendOrganizationHeader: false,
        signal,
      }),
    ...CACHE.list,
  });
}

export function useUpdateSmsSetting(): UseMutationResult<
  PlatformSmsSettingResponse,
  unknown,
  UpdatePlatformSmsSettingRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdatePlatformSmsSettingRequest) =>
      apiRequest({
        path: SETTING_PATH,
        method: 'PUT',
        body,
        schema: platformSmsSettingResponseSchema,
        sendOrganizationHeader: false,
      }),
    onSuccess: (saved) => queryClient.setQueryData(qk.controlRoomSms(), saved),
    meta: { invalidates: [qk.controlRoomActivity(), qk.messagingChannels()] },
  });
}

export function organizationSmsUsageQueryOptions(organizationId: string) {
  return queryOptions({
    queryKey: qk.controlRoomOrganizationSmsUsage(organizationId),
    queryFn: ({ signal }): Promise<SmsUsageResponse> =>
      apiRequest({
        path: `/platform/organizations/${encodeURIComponent(organizationId)}/sms-usage`,
        schema: smsUsageResponseSchema,
        sendOrganizationHeader: false,
        signal,
      }),
    ...CACHE.list,
  });
}
